import { randUuid } from "@/libraries/helper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	FormControl,
	FormHelperText,
	InputAdornment,
	InputLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	OutlinedInput,
	Radio,
	ThemeProvider,
	createTheme,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import BasicButton from "../button/ButtonBasicButton";
import ModalCard from "../modal/ModalCardModal";

const theme = createTheme({
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: "12px",
					".MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#F87171",
					},
				},
				input: {
					color: "#3F3F46",
					"&::placeholder": {
						color: "#3F3F46",
						opacity: 1,
					},
				},
				notchedOutline: {
					"& legend > span": {
						display: "none",
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					fontSize: "16px",
					color: "#3F3F46",
					backgroundColor: "#FFFFFF",
					padding: "4px",
					display: "none",
					"&.Mui-focused": {
						color: "#3F3F46",
					},
					"&.MuiFormLabel-filled": {
						transform: "translate(12px, -10px) scale(0.75)", // Filled position
						display: "block",
					},
					"&.Mui-error": {
						color: "#FCA5A5",
					},
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					fontSize: "24px",
					color: "#3F3F46",
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					"&.Mui-error": {
						color: "#FCA5A5",
					},
				},
			},
		},
		MuiListItem: {
			styleOverrides: {
				root: {
					padding: "8px 0",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				},
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					minWidth: "0",
				},
			},
		},
		MuiButtonBase: {
			styleOverrides: {
				root: {
					padding: 0,
				},
			},
		},
	},
});

type CustomInputProps = {
	label: string;
	value?: any;
	name: string;
	placeholder?: string;
	errorMessage?: string;
	startIcon?: React.ReactNode;
	onChange?: (e: any) => void;
	onBlur?: (e: any) => void;
	options: any;
};

const SelectBase: React.FC<CustomInputProps> = ({
	label,
	value,
	name,
	placeholder,
	errorMessage,
	startIcon,
	onChange,
	onBlur,
	options,
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [selectedValue, setSelectedValue] = useState(value);
	const [otherValue, setOtherValue] = useState("");

	useEffect(() => {
		setSelectedValue(value);
	}, [value]);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		if (selectedValue === "Other Health Care Professionals") {
			if (otherValue !== "") {
				setSelectedValue(otherValue);
				if (onChange) {
					onChange(otherValue);
				}
			}
		} else {
			if (onChange) {
				onChange(selectedValue);
			}
		}
	};

	const handleCancel = () => {
		setOpen(false);
		setSelectedValue("");
		if (onChange) {
			onChange("");
		}
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setOtherValue(e.target.value);
	};

	const handleListItemClick = (value: string) => {
		setSelectedValue(value);
	};

	const filteredOptions = (Array.isArray(options) ? options : []).filter(
		(option) => option.name.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<ThemeProvider theme={theme}>
			<FormControl
				fullWidth
				sx={{ marginBottom: errorMessage ? "20px" : "0", height: "56px" }}
				variant="outlined"
				error={!!errorMessage}
			>
				<InputLabel htmlFor={`${name}-input`} shrink>
					{label}
				</InputLabel>
				<OutlinedInput
					id={`${name}-input`}
					type="text"
					value={selectedValue}
					name={name}
					placeholder={placeholder}
					onChange={onChange}
					startAdornment={
						startIcon && (
							<InputAdornment position="start">{startIcon}</InputAdornment>
						)
					}
					endAdornment={
						<InputAdornment position="end">
							<KeyboardArrowDownIcon />
						</InputAdornment>
					}
					readOnly
					label={label}
					onClick={handleOpen}
				/>
				{errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
			</FormControl>
			<ModalCard isOpen={open} onClose={handleClose}>
				<Box className="container">
					<Box className="absolute wrapper bg-white top-10 inset-x-0 z-10 rounded-t-2xl py-4">
						<Box className="container">
							<p className="text-dark text-base font-bold mb-2">{label}</p>
							<FormControl fullWidth variant="outlined">
								<OutlinedInput
									autoFocus
									type="text"
									placeholder="Search"
									value={search}
									label={label}
									sx={{ width: "100%", height: "56px" }}
									onChange={handleSearchChange}
									startAdornment={
										<InputAdornment position="start">
											{<SearchIcon />}
										</InputAdornment>
									}
								/>
							</FormControl>
						</Box>
					</Box>
					<List className="list">
						{filteredOptions.map((option) => (
							<ListItem
								className={
									option.name === "Other Health Care Professionals"
										? "last-items"
										: "list-items"
								}
								key={randUuid()}
								onClick={() => handleListItemClick(option.name)}
							>
								<ListItemText primary={option.name} />
								<ListItemIcon>
									<Radio
										edge="start"
										checked={selectedValue === option.name}
										tabIndex={-1}
										sx={{ width: "24px", height: "24px" }}
									/>
								</ListItemIcon>
							</ListItem>
						))}
						{selectedValue === "Other Health Care Professionals" && (
							<FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
								<OutlinedInput
									autoFocus
									type="text"
									value={otherValue}
									sx={{ width: "100%", height: "56px" }}
									label={label}
									onChange={handleInputChange}
									onBlur={onBlur}
								/>
							</FormControl>
						)}
					</List>
					<Box className="wrapper absolute w-full bg-white bottom-0 inset-x-0 z-30 py-3">
						<Box className="container flex items-center gap-1">
							<BasicButton
								variant="outlined"
								color="secondary"
								onClick={handleCancel}
								sx={{
									width: "100%",
									height: "48px",
									fontSize: "14px",
									fontWeight: 600,
								}}
							>
								CANCEL
							</BasicButton>
							<BasicButton
								variant="contained"
								color="secondary"
								onClick={handleClose}
								sx={{
									width: "100%",
									height: "48px",
									fontSize: "14px",
									fontWeight: 600,
								}}
							>
								SELECT
							</BasicButton>
						</Box>
					</Box>
				</Box>
			</ModalCard>
		</ThemeProvider>
	);
};

export default SelectBase;
